'use client';

import { accountsService } from '@/@account/services';
import { tracking } from '@/lib/features/tracking';
import {
    Button,
    Chip,
    Input,
    Listbox,
    ListboxItem,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader,
    Spinner,
    useDisclosure,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DarkTooltip } from './dark-tooltip';

export interface MasqueradeClient {
    id: string;
    name: string;
}

/**
 * Internal-only component that lets support users masquerade as a client.
 * Renders the trigger button, active-client chip, and the search modal.
 */
export function ClientMasquerade({
    selectedClient,
    onSelectClient,
    isLocked = false,
}: {
    selectedClient: MasqueradeClient | null;
    onSelectClient: (client: MasqueradeClient | null) => void;
    isLocked?: boolean;
}) {
    const [searchResults, setSearchResults] = useState<MasqueradeClient[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, []);

    const handleSearch = useCallback((term: string) => {
        setSearchTerm(term);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        if (term.length < 2) {
            setIsSearching(false);
            setSearchResults([]);
            return;
        }
        setIsSearching(true);
        searchTimeoutRef.current = setTimeout(async () => {
            try {
                const results = await accountsService.searchClients({
                    searchTerm: term,
                    searchField: 'org_name',
                });
                setSearchResults(results);
            } catch (error) {
                tracking.errorWithSource(error as Error, 'masquerade-client-search');
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 300);
    }, []);

    const handleSelect = useCallback(
        (client: MasqueradeClient) => {
            onSelectClient(client);
            setSearchTerm('');
            setSearchResults([]);
            onClose();
        },
        [onSelectClient, onClose],
    );

    const handleClear = useCallback(() => {
        onSelectClient(null);
    }, [onSelectClient]);

    return (
        <>
            {selectedClient && (
                <Chip
                    variant="flat"
                    color="secondary"
                    size="sm"
                    className="gap-1"
                    onClose={isLocked ? undefined : handleClear}
                    startContent={<Icon icon="lucide:hat-glasses" width={14} />}
                >
                    {selectedClient.name}
                </Chip>
            )}

            {!selectedClient && (
                <DarkTooltip
                    content="Chat as a specific client using their Snowflake data and config. Useful for troubleshooting client-specific issues."
                    size="sm"
                    placement="bottom"
                    classNames={{ content: 'max-w-[220px]' }}
                >
                    <Button
                        isIconOnly
                        onPress={onOpen}
                        variant="flat"
                        size="sm"
                        aria-label="Masquerade as Client"
                        className="h-9 w-9 rounded-full bg-default-100 text-default-500 data-[hover=true]:bg-default-200"
                    >
                        <Icon icon="lucide:hat-glasses" width={18} height={18} />
                    </Button>
                </DarkTooltip>
            )}
            <Modal isOpen={isOpen} onClose={onClose} size="md" placement="center">
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        <span>Masquerade as Client</span>
                        <span className="text-small font-normal text-default-500">
                            Search for and select a client to chat as them
                        </span>
                    </ModalHeader>
                    <ModalBody className="pb-6">
                        <Input
                            placeholder="Search clients by name..."
                            value={searchTerm}
                            onValueChange={handleSearch}
                            startContent={
                                <Icon
                                    icon="lucide:search"
                                    width={16}
                                    className="text-default-400"
                                />
                            }
                            endContent={isSearching ? <Spinner size="sm" /> : null}
                            autoFocus
                            size="sm"
                            variant="bordered"
                        />
                        {searchResults.length > 0 && (
                            <Listbox
                                aria-label="Client search results"
                                className="max-h-64 overflow-auto"
                                onAction={(key) => {
                                    const client = searchResults.find((c) => c.id === key);
                                    if (client) handleSelect(client);
                                }}
                            >
                                {searchResults.map((client) => (
                                    <ListboxItem
                                        key={client.id}
                                        description={`ID: ${client.id}`}
                                        startContent={
                                            <Icon
                                                icon="lucide:building-2"
                                                width={16}
                                                className="text-default-400"
                                            />
                                        }
                                    >
                                        {client.name}
                                    </ListboxItem>
                                ))}
                            </Listbox>
                        )}
                        {searchTerm.length >= 2 && !isSearching && searchResults.length === 0 && (
                            <p className="py-4 text-center text-small text-default-400">
                                No clients found for &quot;{searchTerm}&quot;
                            </p>
                        )}
                        {searchTerm.length > 0 && searchTerm.length < 2 && (
                            <p className="py-4 text-center text-small text-default-400">
                                Type at least 2 characters to search
                            </p>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}

/**
 * Thin banner shown at the top of active conversations when masquerading.
 */
export function MasqueradeBanner({
    client,
    onStop,
    isLocked = false,
}: {
    client: MasqueradeClient;
    onStop: () => void;
    isLocked?: boolean;
}) {
    return (
        <div className="border-b border-secondary-200 bg-secondary-50 px-4 py-2">
            <div className="mx-auto flex max-w-3xl items-center justify-center">
                <div className="flex items-center gap-2 text-small text-secondary-700">
                    <Icon icon="lucide:hat-glasses" width={16} />
                    <span>
                        Masquerading as <strong>{client.name}</strong>
                    </span>
                    {isLocked && (
                        <DarkTooltip
                            content="This conversation is tied to this client's Snowflake connection and cannot be changed."
                            size="sm"
                        >
                            <Icon icon="lucide:lock" width={14} className="text-secondary-400" />
                        </DarkTooltip>
                    )}
                </div>
                {!isLocked && (
                    <Button
                        size="sm"
                        variant="light"
                        color="secondary"
                        onPress={onStop}
                        startContent={<Icon icon="lucide:x" width={14} />}
                    >
                        Stop
                    </Button>
                )}
            </div>
        </div>
    );
}
